/**
 * SourceIcon — Map source string -> Tabler icon + Mantine color.
 *
 * Spec §3.2: 18 V1 sources + 10 V2 sources (TASK-2594) com icones e cores
 * distintas pra reconhecimento rapido no feed.
 *
 * V2 sources (10) chegam via /ingest/* endpoints (TASK-2582+):
 *   cloudtrail, aws_health, aws_backup, cost_anomaly, alb_target_health,
 *   ecs_service, gmud_lifecycle, provisioning_saga, cicd_gitlab, security_auth.
 */
import {
  IconAlertTriangle, IconShieldLock, IconActivityHeartbeat, IconCloudComputing,
  IconDatabase, IconBell, IconClipboardList, IconChecklist, IconGavel,
  IconGitBranch, IconUsers, IconKey, IconServer, IconBrandAws, IconReceipt2,
  IconRadar, IconMail, IconMessageCircle,
  // V2 sources (TASK-2594)
  IconHistory, IconHeartbeat, IconDeviceFloppy, IconTrendingUp, IconRoute,
  IconBoxMultiple, IconCalendarEvent, IconStack, IconGitMerge,
} from '@tabler/icons-react'
import type { TablerIcon } from '@tabler/icons-react'

const SOURCE_ICONS: Record<string, TablerIcon> = {
  // V1 sources (18)
  audit: IconClipboardList,
  notification: IconBell,
  inbox: IconChecklist,
  chamado: IconChecklist,
  cloudwatch_alarm: IconAlertTriangle,
  guardduty: IconShieldLock,
  securityhub: IconShieldLock,
  zabbix: IconActivityHeartbeat,
  ztna: IconRadar,
  provisioning: IconCloudComputing,
  database: IconDatabase,
  rbac: IconKey,
  user: IconUsers,
  ec2: IconServer,
  aws: IconBrandAws,
  finops: IconReceipt2,
  gitlab: IconGitBranch,
  email: IconMail,
  whatsapp: IconMessageCircle,
  // V2 sources (10) — TASK-2594
  cloudtrail: IconHistory,
  aws_health: IconHeartbeat,
  aws_backup: IconDeviceFloppy,
  cost_anomaly: IconTrendingUp,
  alb_target_health: IconRoute,
  ecs_service: IconBoxMultiple,
  gmud_lifecycle: IconCalendarEvent,
  provisioning_saga: IconStack,
  cicd_gitlab: IconGitMerge,
  security_auth: IconKey,
  devops_sre: IconShieldLock,  // vigia de postura/operacao do @DevOpsSRE (MIGRATION_405)
}

/**
 * Mantine color token por source. Usado pra colorir badges/icons no feed
 * e reforcar categoria visualmente. Fallback: 'gray'.
 */
export const SOURCE_COLORS: Record<string, string> = {
  // V1 sources
  audit: 'gray',
  notification: 'blue',
  inbox: 'blue',
  chamado: 'blue',
  cloudwatch_alarm: 'orange',
  guardduty: 'red',
  securityhub: 'red',
  zabbix: 'lime',
  ztna: 'cyan',
  provisioning: 'teal',
  database: 'indigo',
  rbac: 'yellow',
  user: 'gray',
  ec2: 'orange',
  aws: 'orange',
  finops: 'green',
  gitlab: 'pink',
  email: 'blue',
  whatsapp: 'green',
  // V2 sources (TASK-2594)
  cloudtrail: 'orange',
  aws_health: 'red',
  aws_backup: 'blue',
  cost_anomaly: 'green',
  alb_target_health: 'cyan',
  ecs_service: 'indigo',
  gmud_lifecycle: 'violet',
  provisioning_saga: 'teal',
  cicd_gitlab: 'pink',
  security_auth: 'yellow',
  devops_sre: 'red',  // achado de seguranca do @DevOpsSRE (MIGRATION_405)
}

export function getSourceIcon(source: string): TablerIcon {
  return SOURCE_ICONS[source] ?? IconBell
}

export function getSourceColor(source: string): string {
  return SOURCE_COLORS[source] ?? 'gray'
}

export interface SourceIconProps {
  source: string
  size?: number
  color?: string
}

export function SourceIcon({ source, size = 18, color }: SourceIconProps) {
  const Icon = getSourceIcon(source)
  const resolvedColor = color ?? `var(--mantine-color-${getSourceColor(source)}-6)`
  return <Icon size={size} color={resolvedColor} />
}
